import * as React from "react";
import { useNavigate } from "react-router";
import { cn } from "@/lib/utils";

export interface AutoRedirectWithMessageProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Message to display during redirect (e.g., "Redirecting to pairing page...")
   */
  message: string;

  /**
   * Path to redirect to (supports query params and nested paths)
   * Examples: "/pairing", "/pairing?nfcId=123&sig=abc", "/profile/edit"
   */
  redirectPath: string;

  /**
   * Delay in milliseconds before redirecting
   * @default 3000 (3 seconds)
   */
  delay?: number;
}

/**
 * Auto-redirect component with countdown timer and animated message
 *
 * Displays a message with countdown timer, then automatically navigates
 * to the specified path after the delay. Properly cleans up timers on unmount
 * to prevent memory leaks.
 *
 * @example
 * ```tsx
 * // Default 3-second delay
 * <AutoRedirectWithMessage
 *   message="Redirecting to pairing page..."
 *   redirectPath="/pairing?nfcId=123&sig=abc"
 * />
 *
 * // Custom 5-second delay
 * <AutoRedirectWithMessage
 *   message="Success! Redirecting..."
 *   redirectPath="/home"
 *   delay={5000}
 * />
 * ```
 */
export const AutoRedirectWithMessage = React.forwardRef<
  HTMLDivElement,
  AutoRedirectWithMessageProps
>(({ message, redirectPath, delay = 3000, className, ...props }, ref) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = React.useState(Math.ceil(delay / 1000));

  React.useEffect(() => {
    // Calculate initial countdown in seconds
    const initialCountdown = Math.ceil(delay / 1000);
    setCountdown(initialCountdown);

    // Countdown interval (updates every second)
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        const next = prev - 1;
        return next < 0 ? 0 : next;
      });
    }, 1000);

    // Navigation timeout (triggers after delay)
    const redirectTimer = setTimeout(() => {
      navigate(redirectPath);
    }, delay);

    // CRITICAL: Cleanup function to prevent memory leaks
    return () => {
      clearInterval(countdownInterval);
      clearTimeout(redirectTimer);
    };
  }, [navigate, redirectPath, delay]);

  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      aria-label={`${message} Redirecting in ${countdown} seconds`}
      className={cn(
        "flex flex-col items-center justify-center gap-[1.5rem]",
        className
      )}
      {...props}
    >
      {/* Animated spinner */}
      <div
        className="size-12 rounded-full border-4 border-gray-100 border-t-brand-red animate-spin"
        aria-hidden="true"
      />

      {/* Message */}
      <p className="text-center text-gray-900 font-montserrat text-base">
        {message}
      </p>

      {/* Countdown timer */}
      <p className="text-center text-gray-500 font-montserrat text-sm">
        Redirecting in {countdown}...
      </p>
    </div>
  );
});

AutoRedirectWithMessage.displayName = "AutoRedirectWithMessage";
